import { app } from './app';
import { soltarEscalacao, type AlvoArraste, type OrigemArraste } from './escalar';
import { soltarPausa } from './pausa';

/**
 * Arrastar e soltar na janela Escalar Time. Funciona com mouse (arrasta depois de mover ~6 px) e com toque
 * (segure o dedo ~0,3 s no jogador e arraste). Pointer Events, sem biblioteca.
 */

interface Arraste {
  origem: OrigemArraste;
  el: HTMLElement;
  x0: number;
  y0: number;
  ativo: boolean;
  toque: boolean;
  fantasma: HTMLElement | null;
  alvoEl: HTMLElement | null;
  /** arrasto na janela de pausa/intervalo (e não no Escalar time) */
  pausa: boolean;
  timer?: number;
}

const SELETOR_ORIGEM = '.slot-campo:not(.vazio), .slot-banco:not(.vazio), .slot-disp';
const DISTANCIA_MOUSE = 6;
const ESPERA_TOQUE = 300;

let arraste: Arraste | null = null;
let fimDoArraste = 0;

function origemDe(el: HTMLElement): OrigemArraste | null {
  if (el.classList.contains('slot-campo')) return { tipo: 'slot', i: Number(el.dataset.i) };
  if (el.classList.contains('slot-banco')) return { tipo: 'banco', i: Number(el.dataset.i) };
  if (el.classList.contains('slot-disp')) return { tipo: 'disp', id: Number(el.dataset.id) };
  return null;
}

function alvoDe(x: number, y: number): { alvo: AlvoArraste; el: HTMLElement } | null {
  // a camisa é um SVG: aceita qualquer Element, não só HTMLElement
  const sob = document.elementFromPoint(x, y);
  if (!sob) return null;
  const slot = sob.closest<HTMLElement>('.slot-campo');
  if (slot) return { alvo: { tipo: 'slot', i: Number(slot.dataset.i) }, el: slot };
  const banco = sob.closest<HTMLElement>('.slot-banco');
  if (banco) return { alvo: { tipo: 'banco', i: Number(banco.dataset.i) }, el: banco };
  const lista = sob.closest<HTMLElement>('.disponiveis');
  if (lista) return { alvo: { tipo: 'disp' }, el: lista };
  return null;
}

function comecar(a: Arraste, x: number, y: number): void {
  a.ativo = true;
  const f = a.el.cloneNode(true) as HTMLElement;
  f.classList.add('fantasma-arraste');
  f.classList.remove('sel');
  f.style.width = `${a.el.getBoundingClientRect().width}px`;
  f.removeAttribute('data-a');
  document.body.appendChild(f);
  a.fantasma = f;
  a.el.classList.add('arrastando');
  document.body.classList.add('em-arraste');
  mover(a, x, y);
}

function mover(a: Arraste, x: number, y: number): void {
  if (a.fantasma) {
    a.fantasma.style.left = `${x}px`;
    a.fantasma.style.top = `${y}px`;
  }
  const alvo = alvoDe(x, y);
  if (alvo?.el !== a.alvoEl) {
    a.alvoEl?.classList.remove('alvo-arraste');
    alvo?.el.classList.add('alvo-arraste');
    a.alvoEl = alvo?.el ?? null;
  }
}

function limpar(a: Arraste): void {
  window.clearTimeout(a.timer);
  a.fantasma?.remove();
  a.el.classList.remove('arrastando');
  a.alvoEl?.classList.remove('alvo-arraste');
  document.body.classList.remove('em-arraste');
}

export function iniciarArrastar(redesenhar: () => void): void {
  document.addEventListener('pointerdown', (ev) => {
    if (ev.button !== 0 || !(app.escalar || app.pausa)) return;
    const el = (ev.target as HTMLElement).closest<HTMLElement>(SELETOR_ORIGEM);
    if (!el || !el.closest('.bf-dlg.escalar')) return;
    // a janela de pausa/intervalo tem a mesma cara do Escalar time, mas as regras de troca são outras
    const pausa = Boolean(el.closest('.bf-dlg.pausa'));
    if (pausa ? !app.pausa : !app.escalar) return;
    if (el.classList.contains('usado')) return;
    const origem = origemDe(el);
    if (!origem) return;
    const a: Arraste = { origem, el, x0: ev.clientX, y0: ev.clientY, ativo: false, toque: ev.pointerType === 'touch', fantasma: null, alvoEl: null, pausa };
    arraste = a;
    // no toque só começa depois de segurar parado (senão rolar a lista viraria arrasto)
    if (a.toque) a.timer = window.setTimeout(() => arraste === a && comecar(a, a.x0, a.y0), ESPERA_TOQUE);
  });

  document.addEventListener('pointermove', (ev) => {
    const a = arraste;
    if (!a) return;
    if (!a.ativo) {
      const d = Math.hypot(ev.clientX - a.x0, ev.clientY - a.y0);
      if (a.toque) {
        if (d > 10) {
          window.clearTimeout(a.timer); // o dedo andou antes da espera: é rolagem, não arrasto
          arraste = null;
        }
      } else if (d > DISTANCIA_MOUSE) comecar(a, ev.clientX, ev.clientY);
      return;
    }
    ev.preventDefault();
    mover(a, ev.clientX, ev.clientY);
  });

  // com o arrasto de toque ativo, o navegador não pode rolar a página
  document.addEventListener('touchmove', (ev) => {
    if (arraste?.ativo) ev.preventDefault();
  }, { passive: false });

  const terminar = (ev: PointerEvent, cancelado: boolean): void => {
    const a = arraste;
    arraste = null;
    if (!a) return;
    const foiArraste = a.ativo;
    const alvo = foiArraste && !cancelado ? alvoDe(ev.clientX, ev.clientY) : null;
    limpar(a);
    if (!foiArraste) return;
    fimDoArraste = Date.now();
    if (alvo) {
      if (a.pausa) soltarPausa(a.origem, alvo.alvo);
      else soltarEscalacao(a.origem, alvo.alvo);
      redesenhar();
    }
  };
  document.addEventListener('pointerup', (ev) => terminar(ev, false));
  document.addEventListener('pointercancel', (ev) => terminar(ev, true));
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && arraste?.ativo) {
      limpar(arraste);
      arraste = null;
    }
  });

  // o clique que o navegador gera ao soltar não pode selecionar nada
  document.addEventListener('click', (ev) => {
    if (Date.now() - fimDoArraste < 350) {
      ev.stopPropagation();
      ev.preventDefault();
    }
  }, true);
}
